/**
 * Las direcciones que la aplicación atiende fuera de su armazón.
 *
 * Viven en su propio módulo, minúsculo, por una razón concreta: quien decide si
 * hay que cargar una pantalla no puede importar esa pantalla. Cuando
 * `esRutaDeBaja` estaba dentro de `BajaWeb.tsx`, importarla desde `main.tsx`
 * arrastraba el archivo entero al paquete principal y el `lazy()` de al lado
 * dejaba de aplazar nada: la página de baja se bajaba en cada arranque de la
 * aplicación, para no usarse nunca.
 */

/**
 * ¿Esta dirección es la de la baja de cuenta?
 *
 * La dirección es la mitad de esa entrega: una página que responde en `/baja/`
 * y no en `/baja` es una página que quien revisa la tienda no encuentra, y el
 * motivo del rechazo va a ser que no existe. Por eso tolera la barra final y
 * las mayúsculas, que es lo que pasa cuando alguien la escribe a mano.
 */
export function esRutaDeBaja(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').toLowerCase() === '/baja';
}

/**
 * ¿Esta dirección es la de elegir una contraseña nueva?
 *
 * Es donde cae quien toca el enlace del correo de recuperación. Tolera la barra
 * final y las mayúsculas por lo mismo que `esRutaDeBaja`, y además acepta la
 * forma sin la ñ: el enlace lo arma el correo y lo abre el navegador del
 * teléfono, y entre los dos hay demasiados lugares donde una ñ se rompe.
 */
export function esRutaDeNuevaContrasena(pathname: string): boolean {
  const limpio = pathname.replace(/\/+$/, '').toLowerCase();
  return limpio === '/nueva-contrasena' || limpio === '/nueva-contraseña';
}

/**
 * ¿Lo que trae la dirección es un enlace de recuperar la contraseña?
 *
 * Se mira el `#` y no el camino. Es la única señal confiable: el camino lo
 * decide el «Site URL» del panel de Supabase, que puede apuntar a la raíz, y
 * entonces el enlace del correo cae en la aplicación como si nada —con sesión
 * ya abierta y la contraseña vieja todavía válida—. Esto es lo que lo
 * convierte en un callejón del que sólo se sale cambiándola.
 *
 * Supabase pone `type=recovery` en el `#` de ese enlace. Nadie más lo manda.
 */
export function esEnlaceDeRecuperacion(hash: string): boolean {
  const parametros = new URLSearchParams(hash.replace(/^#/, ''));
  return parametros.get('type') === 'recovery';
}

/**
 * ¿Hay que mostrar la pantalla de contraseña nueva en vez de la aplicación?
 *
 * La decisión vive acá, y no suelta en `main.tsx`, por algo concreto: ahí no se
 * puede probar —el módulo dibuja al importarse— y cuando estaba ahí se podía
 * borrar entera sin que ninguna prueba se cayera. Eso es exactamente el enganche
 * que no puede fallar en silencio: si falla, el enlace del correo abre la
 * aplicación con la contraseña vieja todavía válida.
 */
export function hayQueElegirContrasena(pathname: string, hash: string): boolean {
  return esRutaDeNuevaContrasena(pathname) || esEnlaceDeRecuperacion(hash);
}
